import { useState } from 'react';

interface UserAvatarProps {
  name: string;
  avatarUrl?: string | null;
  colorHex?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  className?: string;
  unlockedBadges?: string[]; // Para efectos premium de racha
}

const sizeClasses = {
  xs: 'w-6 h-6 text-xs',
  sm: 'w-8 h-8 text-sm',
  md: 'w-12 h-12 text-base',
  lg: 'w-16 h-16 text-lg',
  xl: 'w-20 h-20 text-xl',
  '2xl': 'w-24 h-24 text-2xl',
};

export default function UserAvatar({ 
  name, 
  avatarUrl, 
  colorHex, 
  size = 'md',
  className = '',
  unlockedBadges = []
}: UserAvatarProps) {
  const [imageError, setImageError] = useState(false);

  const baseUrl = (() => {
    try {
      return (import.meta as any)?.env?.BASE_URL as string | undefined;
    } catch {
      return undefined;
    }
  })();

  const normalizedAvatarUrl = (() => {
    if (!avatarUrl) return avatarUrl;

    if (avatarUrl.startsWith('/avatars/')) {
      const base = typeof baseUrl === 'string' ? baseUrl : '/';
      const normalizedBase = base.endsWith('/') ? base : `${base}/`;
      return `${normalizedBase}${avatarUrl.slice(1)}`;
    }

    return avatarUrl;
  })();
  
  // Obtener inicial del nombre
  const initial = name?.trim().charAt(0).toUpperCase() || '?';
  
  // Color por defecto si no se proporciona
  const bgColor = colorHex || '#3b82f6';

  // Calcular marcos circulares y brillos (glow) de racha
  const premiumClasses = (() => {
    if (!unlockedBadges || unlockedBadges.length === 0) return '';
    
    // Verificar del hito más alto al más bajo
    if (unlockedBadges.includes('leyenda_viviente') || unlockedBadges.includes('guardian_constancia')) {
      return 'border-2 border-rose-500 ring-2 ring-purple-500/50 shadow-[0_0_15px_rgba(244,63,94,0.6)] animate-pulse';
    }
    if (unlockedBadges.includes('maestro_habito')) {
      return 'border-2 border-purple-500 ring-2 ring-purple-400/40 shadow-[0_0_12px_rgba(168,85,247,0.5)] animate-pulse';
    }
    if (unlockedBadges.includes('club_centenario')) {
      return 'border-2 border-yellow-500 ring-2 ring-yellow-400/30 shadow-[0_0_10px_rgba(234,179,8,0.4)]';
    }
    if (unlockedBadges.includes('rutina_acero')) {
      return 'border-2 border-slate-300 ring-2 ring-slate-400/20 shadow-[0_0_8px_rgba(148,163,184,0.3)]';
    }
    if (unlockedBadges.includes('en_llamas')) {
      return 'border-2 border-amber-600 ring-2 ring-amber-500/10 shadow-[0_0_8px_rgba(217,119,6,0.3)]';
    }
    return '';
  })();

  const isIllustrationSvg = !!normalizedAvatarUrl &&
    (normalizedAvatarUrl.endsWith('.svg') || normalizedAvatarUrl.includes('/avatars/illustrations/'));

  const isLikelyImageUrl = !!normalizedAvatarUrl &&
    (normalizedAvatarUrl.startsWith('http://') ||
      normalizedAvatarUrl.startsWith('https://') ||
      normalizedAvatarUrl.startsWith('/') ||
      normalizedAvatarUrl.startsWith('./') ||
      normalizedAvatarUrl.includes('firebasestorage.googleapis.com') ||
      isIllustrationSvg);

  // If avatarUrl is not an URL/path, treat it as emoji/text avatar.
  if (normalizedAvatarUrl && !isLikelyImageUrl) {
    return (
      <div
        className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-bold text-white ${premiumClasses} ${className}`}
        style={{ backgroundColor: bgColor }}
      >
        <span className="leading-none">{normalizedAvatarUrl}</span>
      </div>
    );
  }
  
  // Si hay URL de avatar (imagen) y no ha fallado, mostrar imagen
  if (normalizedAvatarUrl && !imageError) {
    if (isIllustrationSvg) {
      return (
        <div
          className={`${sizeClasses[size]} rounded-full overflow-hidden flex items-center justify-center p-[2px] ${premiumClasses} ${className}`}
          style={{ backgroundColor: bgColor }}
        >
          <img
            src={normalizedAvatarUrl}
            alt={name}
            className="w-full h-full rounded-full object-contain"
            onError={() => setImageError(true)}
          />
        </div>
      );
    }

    return (
      <div
        className={`${sizeClasses[size]} rounded-full overflow-hidden flex items-center justify-center p-[2px] ${premiumClasses} ${className}`}
        style={{ backgroundColor: bgColor }}
      >
        <img
          src={normalizedAvatarUrl}
          alt={name}
          className="w-full h-full rounded-full object-cover"
          onError={() => setImageError(true)}
        />
      </div>
    );
  }
  
  // Si no hay URL o falló, mostrar inicial con color
  return (
    <div
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-bold text-white ${premiumClasses} ${className}`}
      style={{ backgroundColor: bgColor }}
    >
      {initial}
    </div>
  );
}
